/**
 * Configuration Status Utilities
 *
 * Derived status helpers that determine configuration state from actual data
 * rather than maintaining a separate status field.
 *
 * This replaces the ConfigurationStatus enum with a derived approach that
 * uses the presence of candidates and other signals as the source of truth.
 */

import type { Configuration } from '~/types';

/**
 * Display state for configuration status visualization
 */
export type ConfigurationDisplayState =
  | 'ready'
  | 'processing'
  | 'completed'
  | 'error'
  | 'optimizing';

/**
 * Determines the display state of a configuration based on its actual data
 *
 * @param config - The configuration to evaluate
 * @param isProcessing - Optional flag indicating if processing is currently in progress
 * @returns The display state for UI purposes
 */
export function getConfigurationDisplayState(
  config: Configuration,
  isProcessing?: boolean
): ConfigurationDisplayState {
  // If explicitly processing, return processing state
  if (isProcessing) {
    return 'processing';
  }

  // If configuration has candidates, it's been processed
  if (config.candidates && config.candidates.length > 0) {
    // Check if any candidate has optimization indicators
    // Optimized candidates typically have prefixes like "UO-" or contain "optimized"
    const hasOptimized = config.candidates.some(
      (c) =>
        c.name.includes('UO-') ||
        c.name.toLowerCase().includes('optimized') ||
        c.name.toLowerCase().includes('optimization')
    );

    return hasOptimized ? 'optimizing' : 'completed';
  }

  // If no candidates, assume ready for processing
  // Note: Error state would need to be tracked separately if needed
  // For now, errors are handled via try/catch and return values
  return 'ready';
}

/**
 * Gets the Chakra UI color scheme name for a display state
 *
 * @param state - The display state
 * @returns Color scheme name compatible with Chakra UI Badge component
 */
export function getStatusColor(state: ConfigurationDisplayState): string {
  switch (state) {
    case 'ready':
      return 'green';
    case 'processing':
      return 'yellow';
    case 'completed':
      return 'blue';
    case 'optimizing':
      return 'orange';
    case 'error':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Gets a human-readable label for a display state
 *
 * @param state - The display state
 * @returns Human-readable label
 */
export function getStatusLabel(state: ConfigurationDisplayState): string {
  switch (state) {
    case 'ready':
      return 'Ready';
    case 'processing':
      return 'Processing';
    case 'completed':
      return 'Completed';
    case 'optimizing':
      return 'Optimizing';
    case 'error':
      return 'Error';
    default:
      return 'Unknown';
  }
}

/**
 * Checks if a configuration has been processed (has candidates)
 *
 * This is the primary replacement for status === PROCESSING_COMPLETED checks
 *
 * @param config - The configuration to check
 * @returns True if the configuration has been processed
 */
export function isConfigurationProcessed(config: Configuration): boolean {
  return !!(config.candidates && config.candidates.length > 0);
}
